// Pattern evaluation (docs/04 §3): every unlocked pattern independently; wilds substitute;
// "Any Bar" matches at the lowest bar value; smaller same-family patterns inside a paying one are suppressed.
import { B } from '../content/balance';
import { isLike, type GridCellView, type GridView } from '../content/defs';
import { PATTERN_LIST } from '../content/registry';
import { D } from './decimal';
import type { Modifiers } from './modifiers';
import type { Cell, PatternHit, PatternId, SizeId } from './types';

export interface PatternInstance {
  id: PatternId;
  family: string;
  jackpot: boolean;
  cells: Cell[];
}

const geometryCache = new Map<string, PatternInstance[]>();

/** All instances of all patterns for a size (not filtered by unlocks). */
export function allPatternInstances(size: SizeId): PatternInstance[] {
  const cached = geometryCache.get(size);
  if (cached) return cached;
  const { reels, rows } = B.SIZES[size];
  const out: PatternInstance[] = [];
  for (const p of PATTERN_LIST)
    for (const cells of p.geometry(reels, rows, size))
      out.push({ id: p.id, family: p.family, jackpot: !!p.jackpot, cells });
  geometryCache.set(size, out);
  return out;
}

export function activePatterns(size: SizeId, mods: Modifiers): PatternInstance[] {
  return allPatternInstances(size).filter((p) => mods.patterns.has(p.id));
}

export function baseMult(id: PatternId, size: SizeId): number {
  if (id === 'gorge') return B.GORGE_MULT_BY_SIZE[size] ?? B.PATTERN_MULT.gorge;
  return B.PATTERN_MULT[id];
}

export function patternMult(id: PatternId, size: SizeId, mods: Modifiers, hymns: Partial<Record<PatternId, number>>): number {
  return baseMult(id, size) + (hymns[id] ?? 0) * B.HYMN_MULT + (mods.patternMultAdd[id] ?? 0) + mods.patternMultAll;
}

type MatchMods = Pick<Modifiers, 'snakeOil' | 'ratsWild' | 'extraWildFor'>;

// Bars match any Bar; Teeth (Golden too) match any Tooth.
const keyOf = (v: GridCellView, m?: MatchMods): string =>
  v.def.tags.includes('bar') ? 'bar' : v.def.tags.includes('tooth') ? 'tooth' : m?.snakeOil && v.def.rottenFrom ? v.def.rottenFrom : v.def.id;
/** A full wild substitutes for anything (Grin; Rats under the Rat King). */
const fullWild = (v: GridCellView, m?: MatchMods): boolean => !!v.def.wild || (!!m?.ratsWild && isLike(v.def, 'rat'));
/** A partial wild only substitutes for some symbols (Bone for Skull and Tooth). */
const wildFor = (v: GridCellView, key: string, m?: MatchMods): boolean =>
  !!v.def.wildFor && (v.def.wildFor.includes(key) || (m?.extraWildFor.includes(key) ?? false));

export interface MatchInfo {
  matched: string;
  /** Per cell: does it substitute (wild) rather than match on its own? */
  subs: boolean[];
}

export function matchInfo(views: GridCellView[], m?: MatchMods): MatchInfo | null {
  const real = views.filter((v) => !fullWild(v, m));
  if (real.length === 0) return views.length ? { matched: views[0]!.def.id, subs: views.map(() => true) } : null;
  if (real.some((v) => v.def.tags.includes('sigil'))) return null;
  for (const key of new Set(real.map((v) => keyOf(v, m)))) {
    if (!real.every((v) => keyOf(v, m) === key || wildFor(v, key, m))) continue;
    const subs = views.map((v) => fullWild(v, m) || keyOf(v, m) !== key);
    const first = views.find((_, i) => !subs[i])!;
    return { matched: first.def.rottenFrom && m?.snakeOil ? first.def.rottenFrom : first.def.id, subs };
  }
  return null;
}

/** Returns the matched defId, or null when the cells don't all match. */
export function matchCells(views: GridCellView[], m?: MatchMods): string | null {
  return matchInfo(views, m)?.matched ?? null;
}

export interface PatternCtx {
  spinIndex: number;
  held: boolean[];
}

export function evaluatePatterns(
  view: GridView,
  values: number[][],
  size: SizeId,
  mods: Modifiers,
  hymns: Partial<Record<PatternId, number>>,
  ctx: PatternCtx = { spinIndex: 0, held: [] },
): PatternHit[] {
  const hits: (PatternHit & { family: string })[] = [];
  const metronome = mods.metronome && ctx.spinIndex > 0 && ctx.spinIndex % 4 === 0;
  for (const p of activePatterns(size, mods)) {
    const views = p.cells.map((c) => view.at(c.reel, c.row));
    if (views.some((v) => !v)) continue;
    const vs = views as GridCellView[];
    const info = matchInfo(vs, mods);
    if (!info) continue;

    const val = (v: GridCellView) => values[v.cell.reel]![v.cell.row]!;
    const real = vs.filter((_, i) => !info.subs[i]);
    const bars = real.filter((v) => v.def.tags.includes('bar'));
    const mixedBars = bars.length > 0 && new Set(bars.map((b) => b.def.id)).size > 1;
    const barFloor = mixedBars ? Math.min(...bars.map(val)) : Infinity;
    const best = real.length ? Math.max(...real.map((v) => (mixedBars ? barFloor : val(v)))) : 0;

    let valueSum = 0;
    vs.forEach((v, i) => {
      if (info.subs[i] && real.length) valueSum += Math.max(val(v), best);
      else if (mixedBars && v.def.tags.includes('bar')) valueSum += barFloor;
      else valueSum += val(v);
    });
    if (valueSum <= 0) continue;

    let mult = patternMult(p.id, size, mods, hymns);
    if (mods.heldPatternMult && p.cells.some((c) => ctx.held[c.reel])) mult += mods.heldPatternMult;
    // "×2 MULT on patterns containing it": once per pattern, however many Crowns are in it.
    const px = Math.max(1, ...vs.map((v) => v.def.patternX ?? 1));
    if (px !== 1) mult *= px;
    if (mods.grinPatternX !== 1 && vs.some((v) => v.def.wild)) mult *= mods.grinPatternX;
    if (metronome) mult *= 2;
    if (p.jackpot) mult *= mods.jackpotMultX * mods.jackpotFormX;
    const cleanse = real.filter((v) => v.def.cleanse).length >= B.CLEANSE_MIN && real.every((v) => v.def.cleanse);
    if (cleanse) mult *= mods.cleanseMult;

    hits.push({ pattern: p.id, family: p.family, cells: p.cells, matched: info.matched, valueSum, mult, value: D(valueSum).mul(mult), ...(cleanse ? { cleanse } : {}) });
  }

  // Suppress smaller same-family patterns fully contained in a paying one.
  const key = (c: Cell) => `${c.reel},${c.row}`;
  const kept = hits.filter((h) => {
    const mine = new Set(h.cells.map(key));
    return !hits.some(
      (o) => o !== h && o.family === h.family && o.cells.length > h.cells.length && [...mine].every((k) => o.cells.some((c) => key(c) === k)),
    );
  });
  return kept.map(({ family: _f, ...h }) => h);
}
